import type {Filter} from "@app-redux/slices/filtersSlice";
import type {ProfileListRes, ProfileRes} from "@/types/profile";
import {DEFAULT_PAGE_LIST_PORTION, SEARCH_FOR, SORT_BY, SORT_TYPE,} from "@constants/index";
import {api} from "./axiosInstance";

const prepareQueryParameters = (filters: GetProfileListParams) => {
  const {roles_ids = [], reps_ids = [], markets_ids = [], locations_ids = []} = filters;
  const isSortByRelevanceDefault =
    !filters.search_for &&
    !filters.sort_by &&
    !filters.sort_type &&
    (roles_ids.length ||
      reps_ids.length ||
      markets_ids.length ||
      locations_ids.length ||
      filters.search_term?.length);

  const isSortByNameDefault =
    !filters.search_for &&
    !filters.sort_by &&
    !filters.sort_type &&
    (!roles_ids.length ||
      !reps_ids.length ||
      !markets_ids.length ||
      !locations_ids.length ||
      !filters.search_term?.length);

  const mappedSearchFilters = {
    ...(roles_ids.length ? {roles_ids: roles_ids?.join(",")} : {}),
    ...(reps_ids.length ? {reps_ids: reps_ids?.join(",")} : {}),
    ...(markets_ids.length ? {markets_ids: markets_ids?.join(",")} : {}),
    ...(locations_ids.length
      ? {locations_ids: locations_ids?.join(",")}
      : {}),
    page_size: DEFAULT_PAGE_LIST_PORTION,
    ...(isSortByRelevanceDefault ? {search_for: SEARCH_FOR.relevance} : {}),
    ...(isSortByNameDefault
      ? {
        sort_type: filters.sort_type || SORT_TYPE.asc,
        sort_by: filters.sort_by || SORT_BY.name,
      }
      : {}),
  };

  return {
    ...filters,
    ...(filters.search_for === SEARCH_FOR.pageLink
      ? {}
      : mappedSearchFilters),
  };
}

export type GetProfileListParams = Omit<Filter, "categories_values"> & {
  roles_ids?: ProfileRes["roles_ids"];
  reps_ids?: ProfileRes["reps_ids"];
  markets_ids?: ProfileRes["markets_ids"];
  locations_ids?: ProfileRes["locations_ids"];
};

export const apiGetProfileList = async (
  filters: GetProfileListParams
): Promise<ProfileListRes> => {
  try {

    const params = prepareQueryParameters(filters);
    const response = await api.get("/api/v1/profiles", {params});
    return response.data;
  } catch (error) {
    throw error;
  }
};

type ExportJobResponse = { jobId: string };
type ExportStatusResponse = {
  id: string;
  state: "Queued" | "Processing" | "Completed" | "Failed";
  downloadUrl?: string;
  error?: string;
};

export const apiStartExport = async (
  filters: GetProfileListParams
): Promise<ExportJobResponse> => {
  const params = prepareQueryParameters(filters);
  const response = await api.post<ExportJobResponse>("/api/v1/profiles/export", null, { params });
  return response.data;
};

export const apiGetExportStatus = async (
  jobId: string
): Promise<ExportStatusResponse> => {
  const response = await api.get<ExportStatusResponse>(`/api/v1/profiles/export/${jobId}`);
  return response.data;
};

const EXPORT_POLL_INTERVAL = 500;
const EXPORT_TIMEOUT = 30_000;

export const apiExportProfileList = async (
  filters: GetProfileListParams
): Promise<void> => {
  const { jobId } = await apiStartExport(filters);

  const startTime = Date.now();
  while (Date.now() - startTime < EXPORT_TIMEOUT) {
    await new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_INTERVAL));
    const status = await apiGetExportStatus(jobId);

    if (status.state === "Completed" && status.downloadUrl) {
      window.open(status.downloadUrl, "_blank");
      return;
    }

    if (status.state === "Failed") {
      throw new Error(status.error ?? "Export failed");
    }
  }

  throw new Error("Export timed out");
};
